// Library Methods
import { ReactNode, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Constants
import { ExamSectionReportType } from "src/services/exams/types";

// Hooks
import useProgressionAnalysisColumns from "./hooks/useProgressionAnalysisColumns";
import usePatternChartColumns from "./hooks/usePatternChartColumns";

type ProgressionReportColumnsType = {
  examReport?: ExamSectionReportType;
  // Shown beside an exam's columns, e.g. its baseline button
  action?: ReactNode;
};

const ProgressionReportColumns = ({
  examReport,
  action,
}: ProgressionReportColumnsType) => {
  const { progressionAnalysisColumns } = useProgressionAnalysisColumns();
  const { patternChartColumns } = usePatternChartColumns(examReport);

  const columns = useMemo(() => {
    if (!examReport) {
      return progressionAnalysisColumns;
    }
    return patternChartColumns;
  }, [examReport, patternChartColumns, progressionAnalysisColumns]);

  if (!examReport) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
            },
            columnGap: "8px",
          }}
        >
          {columns.map((column) => (
            <Typography key={column.title} fontSize="12px">
              <span style={{ fontWeight: 600 }}>{column.title}:</span>{" "}
              <span
                style={{
                  color: Boolean(column.isBlueText) ? "#2563EB" : "#000",
                }}
              >
                {column.value}
              </span>
            </Typography>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Grid
      item
      xs={12}
      sx={{
        display: "flex",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        alignItems: "flex-start",
        gap: "8px",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
          },
          columnGap: "8px",
        }}
      >
        {columns.map((column) => (
          <Typography key={column.title} fontSize="12px">
            <span style={{ fontWeight: 600 }}>{column.title}:</span>{" "}
            <span
              style={{
                color: Boolean(column.isBlueText) ? "#2563EB" : "#000",
              }}
            >
              {column.value}
            </span>
          </Typography>
        ))}
      </Box>
      {action}
    </Grid>
  );
};

export default ProgressionReportColumns;
